// =============================================================================
// MeuDriver — /presentes: comprar um vale-aula para presentear
// =============================================================================

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Gift, ArrowLeft, Loader2, Mail, MessageSquare, Check, AlertCircle, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useSEO } from '@/hooks/useSEO'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { supabase } from '@/lib/supabase'
import { formatCurrency } from '@/lib/utils'

const AMOUNTS = [100, 150, 200, 300, 500]

/** Mínimo aceito pela edge function `purchase-gift-card` (MIN_CUSTOM). */
const MIN_AMOUNT = 20
const MAX_AMOUNT = 10000

type PaymentStatus = 'success' | 'failure' | 'pending'

export default function GiftCardPage() {
  useSEO({
    title: 'Vale-aula de presente',
    description:
      'Presenteie alguém com aulas práticas de direção. O vale chega por e-mail e vale para qualquer instrutor da plataforma.',
  })

  const [amount, setAmount] = useState<number>(150)
  const [customAmount, setCustomAmount] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  // Retorno da tela do Pix — ver `return_path` em purchase-gift-card.
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null)

  useEffect(() => {
    const status = searchParams.get('status')
    const VALID: PaymentStatus[] = ['success', 'failure', 'pending']
    if (!status || !VALID.includes(status as PaymentStatus)) return
    setPaymentStatus(status as PaymentStatus)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  const effectiveAmount = customAmount ? parseFloat(customAmount) : amount

  async function handleCheckout() {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast.error('Informe o e-mail do presenteado.')
      return
    }
    if (!effectiveAmount || effectiveAmount < MIN_AMOUNT || effectiveAmount > MAX_AMOUNT) {
      toast.error(`Valor deve ser entre ${formatCurrency(MIN_AMOUNT)} e ${formatCurrency(MAX_AMOUNT)}.`)
      return
    }

    setLoading(true)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const jwt = sessionData.session?.access_token

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/purchase-gift-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({
          amount: effectiveAmount,
          recipient_email: recipientEmail,
          message: message.trim() || undefined,
        }),
      })

      const result = await res.json()
      if (!res.ok) {
        throw new Error(result.error ?? 'Erro ao criar vale-aula')
      }

      if (result.payment_url) {
        navigate(result.payment_url)
      } else {
        setDone(true)
        toast.success('Vale-aula enviado com sucesso!')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro inesperado')
    } finally {
      setLoading(false)
    }
  }

  if (done || paymentStatus === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="flex flex-col items-center gap-6 py-12">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Vale-aula enviado!</h2>
              <p className="mt-2 text-gray-600">
                O destinatário receberá o código por e-mail em breve.
              </p>
            </div>
            <Button asChild>
              <Link to="/">Voltar ao início</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (paymentStatus === 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="flex flex-col items-center gap-6 py-12">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Pagamento em análise</h2>
              <p className="mt-2 text-gray-600">
                Assim que o Mercado Pago confirmar, enviamos o código por e-mail
                ao destinatário. Você não precisa fazer mais nada.
              </p>
            </div>
            <Button asChild>
              <Link to="/">Voltar ao início</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-teal-50 px-4 py-12">
      <div className="mx-auto max-w-2xl">
        {/* Back */}
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Link>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-600">
            <Gift className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Vale-aula de presente</h1>
          <p className="mt-2 text-gray-600">
            Presenteie alguém com aulas de direção. O destinatário escolhe o instrutor e o horário.
          </p>
        </div>

        <div className="space-y-6">
          {paymentStatus === 'failure' && (
            <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <div className="text-sm">
                <p className="font-semibold text-red-900">Pagamento não concluído</p>
                <p className="mt-0.5 text-red-700">
                  O pagamento não foi aprovado e nenhum valor foi cobrado. Você
                  pode tentar novamente abaixo.
                </p>
              </div>
            </div>
          )}

          {/* Amount */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Valor do presente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-5 gap-2">
                {AMOUNTS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => { setAmount(v); setCustomAmount('') }}
                    className={`rounded-lg border-2 py-3 text-sm font-semibold transition-colors ${
                      amount === v && !customAmount
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {formatCurrency(v)}
                  </button>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="custom-amount">Ou informe outro valor (R$)</Label>
                <Input
                  id="custom-amount"
                  type="number"
                  min={30}
                  max={10000}
                  placeholder="Ex: 250"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                />
              </div>
              {effectiveAmount > 0 && (
                <p className="text-sm text-gray-500">
                  Total a pagar: <span className="font-semibold text-gray-900">{formatCurrency(effectiveAmount)}</span>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recipient */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Para quem é o presente?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="recipient-email">
                  <Mail className="mr-1.5 inline h-4 w-4" />
                  E-mail do destinatário
                </Label>
                <Input
                  id="recipient-email"
                  type="email"
                  placeholder="nome@email.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  O código do vale-aula será enviado para esse e-mail.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="message">
                  <MessageSquare className="mr-1.5 inline h-4 w-4" />
                  Mensagem (opcional)
                </Label>
                <Textarea
                  id="message"
                  placeholder="Feliz aniversário! Espero que aproveite as aulas 🎉"
                  rows={3}
                  maxLength={300}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <p className="text-right text-xs text-gray-400">{message.length}/300</p>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Button
            className="w-full py-6 text-base"
            onClick={handleCheckout}
            disabled={loading || !recipientEmail}
          >
            {loading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <Gift className="mr-2 h-5 w-5" />
            )}
            {loading ? 'Aguarde...' : `Presentear com ${effectiveAmount > 0 ? formatCurrency(effectiveAmount) : '...'}`}
          </Button>

          <p className="text-center text-xs text-gray-500">
            O pagamento é processado com segurança pelo Mercado Pago.
            O vale-aula tem validade de 1 ano.
          </p>
        </div>
      </div>
    </div>
  )
}
